import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import Button from './ui/Button';
import ToggleSwitch from './forms/ToggleSwitch';
import { formatPrice } from '../utils/coursePricing';
import type {
  ScheduleAction,
  ScheduleCharge,
  SchedulePreview,
  SchedulePreviewEvent,
  SchedulePreviewTemplate,
  ScheduleSelection,
} from '../types/schedulePrompt';
import './SchedulePromptModal.css';

const formatDateTime = (value: string, locale: string) =>
  new Date(value).toLocaleString(locale || undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

// =============================================================================
// CHARGES LIST (also used after a member's self-leave)
// =============================================================================

interface ScheduleChargesListProps {
  charges: ScheduleCharge[];
  memberNames?: Record<string, string>;
  /** Explains that nothing was cancelled and who acts next. */
  note: string;
  /** Admin link to the payments UI, where a charge can be cancelled. */
  paymentsLink?: string;
}

export const ScheduleChargesList: React.FC<ScheduleChargesListProps> = ({
  charges,
  memberNames,
  note,
  paymentsLink,
}) => {
  const { t, i18n } = useTranslation();
  if (charges.length === 0) return null;

  return (
    <section className="schedule-prompt__charges" aria-label={t('schedulePrompt.chargesTitle')}>
      <h3 className="schedule-prompt__heading">
        {t('schedulePrompt.chargesTitle')} ({charges.length})
      </h3>
      <ul className="schedule-prompt__list" data-testid="schedule-prompt-charges">
        {charges.map((c) => (
          <li key={c.intentId} className="schedule-prompt__row schedule-prompt__row--static">
            <div className="schedule-prompt__row-main">
              <span className="schedule-prompt__row-title">
                {memberNames?.[String(c.memberId)] ? `${memberNames[String(c.memberId)]} · ` : ''}
                {c.eventName}
              </span>
              <span className="schedule-prompt__row-meta">
                {formatDateTime(c.eventStartTime, i18n.language)}
              </span>
            </div>
            <div className="schedule-prompt__charge-amounts">
              <span>{formatPrice(c.amount, i18n.language)}</span>
              {c.paidAmount > 0 && (
                <span className="schedule-prompt__row-meta">
                  {t('schedulePrompt.paidAmount', { amount: formatPrice(c.paidAmount, i18n.language) })}
                </span>
              )}
              <span className={`badge schedule-prompt__status schedule-prompt__status--${c.status}`}>
                {t(`schedulePrompt.chargeStatus.${c.status}`)}
              </span>
            </div>
          </li>
        ))}
      </ul>
      <p className="schedule-prompt__note">
        {note}
        {paymentsLink && (
          <>
            {' '}
            <Button as="link" to={paymentsLink} variant="ghost" size="sm">
              {t('schedulePrompt.openPayments')}
            </Button>
          </>
        )}
      </p>
    </section>
  );
};

// =============================================================================
// PROMPT
// =============================================================================

export interface SchedulePromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  action: ScheduleAction;
  title: string;
  /** Who is being added / removed, shown at the top. */
  memberLabel?: string;
  preview: SchedulePreview | null;
  previewLoading?: boolean;
  previewError?: string | null;
  /** False = list only, no ticking (course un-enroll is the full cascade). */
  selectable: boolean;
  /** Course un-enroll copy: the member leaves the season and all its trainings. */
  unenroll?: boolean;
  /** Receives the ticked items, or null when the list was not selectable. */
  onConfirm: (selection: ScheduleSelection | null) => void;
  confirming?: boolean;
  memberNames?: Record<string, string>;
  paymentsLink?: string;
}

const isSuggested = (item: { suggested?: boolean }) => item.suggested !== false;

const SchedulePromptModal: React.FC<SchedulePromptModalProps> = ({
  isOpen,
  onClose,
  action,
  title,
  memberLabel,
  preview,
  previewLoading = false,
  previewError = null,
  selectable,
  unenroll = false,
  onConfirm,
  confirming = false,
  memberNames,
  paymentsLink,
}) => {
  const { t, i18n } = useTranslation();

  const templates = useMemo(() => preview?.templates ?? [], [preview]);
  // Events of a listed template follow that template; the rest stand alone.
  const looseEvents = useMemo<SchedulePreviewEvent[]>(() => {
    const listed = new Set(templates.map((tpl) => tpl.templateId));
    return (preview?.events ?? []).filter((e) => e.templateId == null || !listed.has(e.templateId));
  }, [preview, templates]);

  const [ticked, setTicked] = useState<{ templates: Set<number>; events: Set<number> }>({
    templates: new Set(),
    events: new Set(),
  });

  // Suggested items start ticked; "other upcoming" (suggested: false) do not.
  useEffect(() => {
    setTicked({
      templates: new Set(templates.filter(isSuggested).map((tpl) => tpl.templateId)),
      events: new Set(looseEvents.filter(isSuggested).map((e) => e.eventId)),
    });
  }, [templates, looseEvents]);

  const otherTemplates = templates.filter((tpl) => !isSuggested(tpl));
  const otherEvents = looseEvents.filter((e) => !isSuggested(e));
  const hasOther = otherTemplates.length > 0 || otherEvents.length > 0;

  const toggle = (kind: 'templates' | 'events', id: number, on: boolean) => {
    setTicked((prev) => {
      const next = new Set(prev[kind]);
      if (on) next.add(id);
      else next.delete(id);
      return { ...prev, [kind]: next };
    });
  };

  const setAll = (on: boolean) =>
    setTicked({
      templates: new Set(on ? templates.map((tpl) => tpl.templateId) : []),
      events: new Set(on ? looseEvents.map((e) => e.eventId) : []),
    });

  const isOn = (kind: 'templates' | 'events', id: number) => !selectable || ticked[kind].has(id);

  const scheduleCount = templates.filter((tpl) => isOn('templates', tpl.templateId)).length;
  const eventCount =
    templates.reduce((sum, tpl) => sum + (isOn('templates', tpl.templateId) ? tpl.futureEventCount : 0), 0) +
    looseEvents.filter((e) => isOn('events', e.eventId)).length;
  const hasItems = templates.length > 0 || looseEvents.length > 0;
  const charges = preview?.charges ?? [];

  const handleConfirm = () => {
    if (!selectable) {
      onConfirm(null);
      return;
    }
    onConfirm({
      templateIds: templates.filter((tpl) => ticked.templates.has(tpl.templateId)).map((tpl) => tpl.templateId),
      eventIds: looseEvents.filter((e) => ticked.events.has(e.eventId)).map((e) => e.eventId),
    });
  };

  const question = unenroll
    ? t('schedulePrompt.unenrollQuestion', { events: eventCount, schedules: scheduleCount })
    : t(action === 'add' ? 'schedulePrompt.addQuestion' : 'schedulePrompt.removeQuestion', {
        events: eventCount,
        schedules: scheduleCount,
      });

  const renderRow = (
    key: string,
    titleText: string,
    meta: React.ReactNode,
    kind: 'templates' | 'events',
    id: number,
  ) => (
    <li key={key} className={`schedule-prompt__row${selectable ? '' : ' schedule-prompt__row--static'}`}>
      <div className="schedule-prompt__row-main">
        <span className="schedule-prompt__row-title">{titleText}</span>
        <span className="schedule-prompt__row-meta">{meta}</span>
      </div>
      {selectable && (
        <ToggleSwitch
          checked={ticked[kind].has(id)}
          onChange={(on) => toggle(kind, id, on)}
          disabled={confirming}
          name={`${kind}-${id}`}
          ariaLabel={titleText}
        />
      )}
    </li>
  );

  const renderTemplateRow = (tpl: SchedulePreviewTemplate) =>
    renderRow(
      `t-${tpl.templateId}`,
      tpl.name,
      <>
        {t('schedulePrompt.upcomingEvents', { count: tpl.futureEventCount })}
        {!tpl.isActive && <span className="badge schedule-prompt__inactive">{t('schedulePrompt.inactive')}</span>}
      </>,
      'templates',
      tpl.templateId,
    );

  const renderEventRow = (e: SchedulePreviewEvent) =>
    renderRow(`e-${e.eventId}`, e.name, formatDateTime(e.startTime, i18n.language), 'events', e.eventId);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="large" dismissible={!confirming}>
      <div className="schedule-prompt">
        {memberLabel && <p className="schedule-prompt__members">{memberLabel}</p>}

        {previewLoading ? (
          <p className="schedule-prompt__muted">{t('common.loading')}</p>
        ) : previewError ? (
          <p className="error-message">{t('common.error')}: {previewError}</p>
        ) : (
          <>
            {unenroll && <p className="schedule-prompt__warning">{t('schedulePrompt.unenrollWarning')}</p>}

            {hasItems ? (
              <>
                <p className="schedule-prompt__question">{question}</p>

                {selectable && (
                  <div className="schedule-prompt__bulk">
                    <Button variant="ghost" size="sm" onClick={() => setAll(true)} disabled={confirming}>
                      {t('common.selectAll', 'Select all')}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setAll(false)} disabled={confirming}>
                      {t('common.deselectAll', 'Deselect all')}
                    </Button>
                  </div>
                )}

                {templates.some(isSuggested) && (
                  <section>
                    <h3 className="schedule-prompt__heading">{t('schedulePrompt.schedulesTitle')}</h3>
                    <ul className="schedule-prompt__list" data-testid="schedule-prompt-templates">
                      {templates.filter(isSuggested).map(renderTemplateRow)}
                    </ul>
                  </section>
                )}

                {looseEvents.some(isSuggested) && (
                  <section>
                    <h3 className="schedule-prompt__heading">{t('schedulePrompt.eventsTitle')}</h3>
                    <ul className="schedule-prompt__list" data-testid="schedule-prompt-events">
                      {looseEvents.filter(isSuggested).map(renderEventRow)}
                    </ul>
                  </section>
                )}

                {hasOther && (
                  <section>
                    <h3 className="schedule-prompt__heading">{t('schedulePrompt.otherTitle')}</h3>
                    <p className="schedule-prompt__hint">{t('schedulePrompt.otherHint')}</p>
                    <ul className="schedule-prompt__list" data-testid="schedule-prompt-other">
                      {otherTemplates.map(renderTemplateRow)}
                      {otherEvents.map(renderEventRow)}
                    </ul>
                  </section>
                )}
              </>
            ) : (
              <p className="schedule-prompt__muted">
                {t(unenroll ? 'schedulePrompt.unenrollNothingScheduled' : 'schedulePrompt.nothingAffected')}
              </p>
            )}

            <ScheduleChargesList
              charges={charges}
              memberNames={memberNames}
              note={t('schedulePrompt.chargesNote')}
              paymentsLink={paymentsLink}
            />
          </>
        )}

        <div className="schedule-prompt__actions">
          <Button variant="secondary" onClick={onClose} disabled={confirming}>
            {t('common.cancel')}
          </Button>
          <Button
            variant={action === 'remove' ? 'danger' : 'primary'}
            onClick={handleConfirm}
            loading={confirming}
            disabled={previewLoading || !!previewError || !preview}
          >
            {unenroll
              ? t('schedulePrompt.confirmUnenroll')
              : t(action === 'add' ? 'schedulePrompt.confirmAdd' : 'schedulePrompt.confirmRemove')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default SchedulePromptModal;
